import { useCallback, useEffect, useRef, useState } from 'react';

const EXIT_MS = 200;

/**
 * Chiusura animata di un pannello laterale.
 *
 * Smontare il pannello all'istante lo fa sparire di colpo, mentre entra scorrendo:
 * l'asimmetria si nota. Qui la chiusura viene ritardata quanto dura l'animazione
 * di uscita, e chi usa l'hook applica la classe restituita.
 *
 * `prefers-reduced-motion` bypassa l'attesa: chi ha chiesto meno movimento vuole
 * anche meno attesa.
 */
export function useAnimatedDismiss(isOpen: boolean, onClose: () => void) {
  const [closing, setClosing] = useState(false);
  const timer = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(() => {
    if (isOpen) setClosing(false);
  }, [isOpen]);

  useEffect(() => () => { if (timer.current) clearTimeout(timer.current); }, []);

  const dismiss = useCallback(() => {
    if (timer.current) return; // chiusura gia` in corso: non accodare un secondo timer
    const reduced = typeof window !== 'undefined'
      && window.matchMedia?.('(prefers-reduced-motion: reduce)').matches;
    if (reduced) { onClose(); return; }

    setClosing(true);
    timer.current = setTimeout(() => {
      timer.current = null;
      setClosing(false);
      onClose();
    }, EXIT_MS);
  }, [onClose]);

  return {
    closing,
    dismiss,
    panelClass: closing ? 'mail-link-panel-out' : 'mail-link-panel-in',
    scrimClass: closing ? 'mail-link-scrim-out' : 'mail-link-scrim-in',
  };
}
